import { useField } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { ViewIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, FieldErrorMessage, Label, Paragraph, SettingsSection } from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import DetailsTableFormValue, {
	DetailsTableFormValueTypes,
} from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import TalentReviewTemplatePreviewModal from '../../../talent-reviews/v2/templates/TalentReviewTemplatePreviewModal';
import MomentTemplateVariations from './MomentTemplateVariations';

type Props = {
	name: string;
};

const useVariationRoles = () => {
	const { t } = useTranslation();

	return useMemo(
		() => [
			{
				value: graphql.RoleType.PrimaryManager,
				label: t('roles.management-primary-manager'),
			},
			{
				value: graphql.RoleType.CollaborativeManager,
				label: t('roles.management-collaborative-manager'),
			},
			{
				value: graphql.RoleType.Coach,
				label: t('roles.management-coach'),
			},
			{
				value: graphql.RoleType.Buddy,
				label: t('roles.management-buddy'),
			},
		],
		[t],
	);
};

const MomentTemplateEditorFeedbackSettings = ({ name }: Props) => {
	const { t } = useTranslation();

	const { data } = graphql.useFeedbackTemplatesQuery({
		variables: {
			isArchived: false,
		},
	});

	const { data: jobTitlesData } = graphql.useJobTitlesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const feedbackTemplates = useMemo(
		() =>
			data?.feedbackTemplates.edges
				.map((edge) => ({
					label: edge.node.title[0].text,
					value: edge.node.id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [],
		[data?.feedbackTemplates.edges],
	);

	const jobTitles = useMemo(
		() =>
			jobTitlesData?.jobTitles.edges
				.map((edge) => ({
					label: edge.node.title[0].text,
					value: edge.node.id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [],
		[jobTitlesData?.jobTitles.edges],
	);

	const roles = useVariationRoles();

	return (
		<>
			<DetailsTableForm
				formValues={[
					{
						label: t('common.settings.momentTemplates.edit.minimumQuestions'),
						name: `${name}.feedback.minimumQuestions`,
						type: DetailsTableFormValueTypes.Number,
					},
					{
						label: t('common.settings.momentTemplates.edit.minimumRecipients'),
						name: `${name}.feedback.minimumRecipients`,
						type: DetailsTableFormValueTypes.Number,
					},
					{
						label: t('common.settings.momentTemplates.edit.maximumRecipients'),
						name: `${name}.feedback.maximumRecipients`,
						type: DetailsTableFormValueTypes.Number,
					},
				]}
			/>

			<Box mt={3}>
				<MomentTemplateVariations
					momentType={graphql.MomentType.Feedback}
					defaultTemplateName={`${name}.feedback.feedbackTemplate.id`}
					name={`${name}.variations`}
					templates={feedbackTemplates}
					jobTitles={jobTitles}
					roles={roles}
				/>
			</Box>
		</>
	);
};

const MomentTemplateEditorGoalSettings = ({ name }: { name: string }) => {
	const { t } = useTranslation();

	const [{ value: totalGoals }] = useField(`${name}.goals.minimumGoals`);
	const [{ value: competencyGoals }] = useField(`${name}.goals.minimumCompetencyGoals`);
	const [{ value }] = useField(`${name}.goals.allowCompetencyGoals`);

	const hasCompetencyGoals = useFeature(graphql.FeatureName.CompetencyGoals);

	return (
		<>
			<DetailsTableForm
				formValues={[
					{
						label: t('common.settings.momentTemplates.edit.minimumGoals'),
						name: `${name}.goals.minimumGoals`,
						type: DetailsTableFormValueTypes.Number,
					},
					...(hasCompetencyGoals
						? [
								{
									label: t('common.settings.momentTemplates.edit.allowCompetencyGoals'),
									name: `${name}.goals.allowCompetencyGoals`,
									type: DetailsTableFormValueTypes.Switch,
								},
							]
						: []),
					...(value
						? [
								{
									label: t('common.settings.momentTemplates.edit.minimumCompetencyGoals'),
									name: `${name}.goals.minimumCompetencyGoals`,
									type: DetailsTableFormValueTypes.Number,
								},
							]
						: []),
				]}
			/>
			{value && competencyGoals > totalGoals ? (
				<Paragraph color="red">
					{t('common.settings.momentTemplates.edit.competencyGoalsCountInconsistency', {
						competencyGoals,
						totalGoals,
					})}
				</Paragraph>
			) : (
				value && (
					<Paragraph>
						{t('common.settings.momentTemplates.edit.competencyGoalsCountExplanation', {
							competencyGoals: competencyGoals || 0,
							totalGoals,
							regularGoals: totalGoals - competencyGoals,
						})}
					</Paragraph>
				)
			)}
		</>
	);
};

const MomentTemplateEditorEvaluationSettings = ({ name }: Props) => {
	const { localize } = useLocalize();
	const { data: evaluationFormsData } = graphql.useEvaluationFormsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const { data: jobTitlesData } = graphql.useJobTitlesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const evaluationForms = evaluationFormsData?.evaluationForms.edges
		.map((edge) => ({
			label: localize(edge.node.title).text,
			value: edge.node.id,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	const jobTitles = useMemo(
		() =>
			jobTitlesData?.jobTitles.edges
				.map((edge) => ({
					label: edge.node.title[0].text,
					value: edge.node.id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [],
		[jobTitlesData?.jobTitles.edges],
	);

	const roles = useVariationRoles();

	if (!evaluationForms) return null;

	return (
		<MomentTemplateVariations
			momentType={graphql.MomentType.Evaluation}
			defaultTemplateName={`${name}.evaluation.evaluationForm.id`}
			name={`${name}.variations`}
			templates={evaluationForms}
			jobTitles={jobTitles}
			roles={roles}
		/>
	);
};

const MomentTemplateEditorMessageSettings = ({ name }: Props) => {
	const { t } = useTranslation();

	return (
		<DetailsTableForm
			formValues={[
				{
					label: t('common.settings.momentTemplates.edit.showToUser'),
					name: `${name}.showToUser`,
					type: DetailsTableFormValueTypes.Switch,
				},
			]}
		/>
	);
};

const MomentTemplateEditorTalentReviewSettings = ({ name }: Props) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const fieldName = `${name}.talentReview.template.id`;
	const [{ value: templateID }] = useField<string | undefined>(fieldName);
	const [previewOpen, setPreviewOpen] = useState(false);

	const { data } = graphql.useTalentReviewTemplatesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const templates = useMemo(
		() =>
			data?.talentReviewTemplates.edges
				.map((edge) => ({
					label: localize(edge.node.title).text,
					value: edge.node.id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [],
		[data?.talentReviewTemplates.edges, localize],
	);

	const handlePreviewOpen = () => setPreviewOpen(true);
	const handlePreviewClose = () => setPreviewOpen(false);

	return (
		<>
			<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
				<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
					<Label>{t('common.settings.momentTemplates.edit.talentReviewTemplate')}</Label>
				</Box>
				<Flex width={[12 / 12, 12 / 12, 9 / 12]} alignItems="center">
					<Box flex={1} minWidth={0}>
						<DetailsTableFormValue
							name={fieldName}
							type={DetailsTableFormValueTypes.Select}
							options={templates}
						/>
					</Box>
					<Button
						ml={2}
						small
						type="secondary"
						icon={<ViewIcon />}
						title={t('common.settings.momentTemplates.edit.talentReviewGridPreview')}
						disabled={!templateID}
						aria-haspopup="dialog"
						aria-expanded={previewOpen}
						onClick={handlePreviewOpen}
					/>
				</Flex>
			</Flex>
			<FieldErrorMessage name={fieldName} />
			<Box mt={2}>
				<Paragraph color="slateBlue">
					{t('common.settings.momentTemplates.edit.talentReviewAudienceHint')}
				</Paragraph>
			</Box>
			{previewOpen && templateID && (
				<TalentReviewTemplatePreviewModal templateID={templateID} onClose={handlePreviewClose} />
			)}
		</>
	);
};

const mapping = {
	[graphql.MomentType.Evaluation]: MomentTemplateEditorEvaluationSettings,
	[graphql.MomentType.Feedback]: MomentTemplateEditorFeedbackSettings,
	[graphql.MomentType.Goals]: MomentTemplateEditorGoalSettings,
	[graphql.MomentType.Message]: MomentTemplateEditorMessageSettings,
	[graphql.MomentType.TalentReview]: MomentTemplateEditorTalentReviewSettings,
};

export default function MomentTemplateEditorTypeSettings({
	name,
	type,
}: {
	name: string;
	type: graphql.MomentType;
}) {
	const { t } = useTranslation();

	const Component = mapping[type];

	return (
		<SettingsSection
			title={
				<>
					{t(`common.settings.cycleTemplates.momentTypes.${type.toLowerCase()}`)}&nbsp;
					{t('common.settings.momentTemplates.edit.details').toLowerCase()}
				</>
			}
		>
			<Component name={name} />
		</SettingsSection>
	);
}
